import React, { useState, useRef } from 'react';
import axios from 'axios';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../ui/dialog';
import { Button } from '../../ui/button';
import { Badge } from '../../ui/badge';
import { Textarea } from '../../ui/textarea';
import { Download, Upload, Check, X, Loader2, FileText, History } from 'lucide-react';
import { toast } from 'sonner';
import { STATUS_CONFIG, docStatus, sourceLabel, fmtDate } from './docCatalog';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PreviewDocumentModal = ({ open, onOpenChange, operationId, versions = [], label, onChanged, canValidate = true }) => {
  const [busy, setBusy] = useState(false);
  const [reason, setReason] = useState('');
  const [showReject, setShowReject] = useState(false);
  const inputRef = useRef(null);
  const doc = versions[0];
  if (!doc) return null;

  const fileUrl = `${BACKEND_URL}${doc.filePath}`;
  const isImage = /\.(png|jpe?g|webp|gif)$/i.test(doc.fileName || doc.filePath || '');
  const st = docStatus(versions);

  const validate = async (status, rsn) => {
    setBusy(true);
    try {
      await axios.put(`${API}/operations/${operationId}/documents/${doc.id}/validate`, { status, reason: rsn });
      toast.success(status === 'validated' ? 'Document validé' : 'Document refusé');
      onChanged?.();
      onOpenChange(false);
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Action impossible');
    } finally { setBusy(false); }
  };

  const replace = async (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', f);
      fd.append('documentType', doc.documentType);
      await axios.post(`${API}/operations/${operationId}/documents`, fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      toast.success('Document remplacé (nouvelle version)');
      onChanged?.();
      onOpenChange(false);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Remplacement impossible');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-3xl" data-testid="preview-document-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileText className="w-5 h-5 text-gray-500" /> {label || doc.fileName}
            <Badge variant="outline" className={`ml-2 text-[10px] ${STATUS_CONFIG[st]?.cls}`}>{STATUS_CONFIG[st]?.label}</Badge>
          </DialogTitle>
        </DialogHeader>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div className="md:col-span-2 bg-gray-50 rounded-xl overflow-hidden border" style={{ minHeight: 360 }}>
            {isImage ? (
              <img src={fileUrl} alt={doc.fileName} className="w-full h-full object-contain max-h-[420px]" />
            ) : (
              <iframe title="preview" src={fileUrl} className="w-full" style={{ height: 420 }} />
            )}
          </div>

          <div className="space-y-3 text-sm">
            <div>
              <p className="text-xs text-gray-400">Fichier</p>
              <p className="font-medium text-gray-800 break-all">{doc.fileName}</p>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div><p className="text-xs text-gray-400">Source</p><p>{sourceLabel(doc)}</p></div>
              <div><p className="text-xs text-gray-400">Version</p><p>v{doc.version}</p></div>
              <div><p className="text-xs text-gray-400">Ajouté le</p><p>{fmtDate(doc.uploadedAt)}</p></div>
              <div><p className="text-xs text-gray-400">Par</p><p className="truncate">{doc.uploadedByName}</p></div>
            </div>
            {doc.notes && <div><p className="text-xs text-gray-400">Commentaire</p><p className="text-gray-700">{doc.notes}</p></div>}
            {doc.rejectionReason && <div><p className="text-xs text-red-400">Motif de refus</p><p className="text-red-600">{doc.rejectionReason}</p></div>}

            {versions.length > 1 && (
              <div className="pt-2 border-t">
                <p className="text-xs text-gray-400 flex items-center gap-1 mb-1"><History className="w-3 h-3" /> Historique ({versions.length} versions)</p>
                {versions.map((v) => (
                  <div key={v.id} className="text-xs text-gray-500 flex justify-between">
                    <span>v{v.version} — {sourceLabel(v)}</span><span>{fmtDate(v.uploadedAt)}</span>
                  </div>
                ))}
              </div>
            )}

            {showReject && (
              <Textarea value={reason} onChange={(e) => setReason(e.target.value)} rows={2} placeholder="Motif du refus…" data-testid="reject-reason" />
            )}
          </div>
        </div>

        <div className="flex flex-wrap gap-2 justify-end pt-2 border-t mt-2">
          <a href={fileUrl} download target="_blank" rel="noopener noreferrer">
            <Button variant="outline" size="sm"><Download className="w-4 h-4 mr-1.5" /> Télécharger</Button>
          </a>
          <input ref={inputRef} type="file" hidden onChange={replace} accept=".pdf,.jpg,.jpeg,.png,.doc,.docx" />
          <Button variant="outline" size="sm" disabled={busy} onClick={() => inputRef.current?.click()} data-testid="preview-replace-btn">
            <Upload className="w-4 h-4 mr-1.5" /> Remplacer
          </Button>
          {canValidate && (showReject ? (
            <Button size="sm" className="bg-red-600 hover:bg-red-700" disabled={busy} onClick={() => validate('rejected', reason)} data-testid="confirm-reject-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <X className="w-4 h-4 mr-1.5" />} Confirmer le refus
            </Button>
          ) : (
            <Button variant="outline" size="sm" className="border-red-200 text-red-600 hover:bg-red-50" disabled={busy} onClick={() => setShowReject(true)} data-testid="preview-reject-btn">
              <X className="w-4 h-4 mr-1.5" /> Refuser
            </Button>
          ))}
          {canValidate && (
            <Button size="sm" className="bg-emerald-600 hover:bg-emerald-700" disabled={busy} onClick={() => validate('validated')} data-testid="preview-validate-btn">
              {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Check className="w-4 h-4 mr-1.5" />} Valider
            </Button>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default PreviewDocumentModal;
